import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	Paper,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { useAppContext, ACTION_TYPES } from '../Context/AppContext';
import { loginUser } from '../Shared/Api';
import { getAuthenticatedHomePath } from '../Shared/homePath';
import AuthPageLayout, { AuthHeaderLink } from '../Components/AuthPageLayout';

export const PORTFOLIO_DEMO_USERNAME = 'demo@bidzbase.com';
export const PORTFOLIO_DEMO_PASSWORD = 'portfolio-demo';

const Login = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const isDemoLogin = searchParams.get('demo') === '1';
	const wasDeactivated = searchParams.get('deactivated') === '1';
	const { dispatch, state } = useAppContext();
	const [formData, setFormData] = useState({
		username: '',
		password: '',
	});
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		dispatch({
			type: ACTION_TYPES.SET_PAGE_TITLE,
			payload: { pageTitle: 'Sign in' },
		});
		return () => {
			dispatch({
				type: ACTION_TYPES.SET_PAGE_TITLE,
				payload: { pageTitle: '' },
			});
		};
	}, [dispatch]);

	useEffect(() => {
		if (state.isAuthenticated) {
			navigate(getAuthenticatedHomePath(state.user));
		}
	}, [navigate, state.isAuthenticated, state.user]);

	useEffect(() => {
		if (!isDemoLogin) {
			return;
		}
		setFormData({
			username: PORTFOLIO_DEMO_USERNAME,
			password: PORTFOLIO_DEMO_PASSWORD,
		});
	}, [isDemoLogin]);

	const handleChange = (e) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value,
		});
		setError('');
	};

	const fillDemoCredentials = () => {
		setFormData({
			username: PORTFOLIO_DEMO_USERNAME,
			password: PORTFOLIO_DEMO_PASSWORD,
		});
		setError('');
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		setLoading(true);

		try {
			const loginResponse = await loginUser(formData, dispatch);
			navigate(getAuthenticatedHomePath(loginResponse.user));
		} catch (err) {
			setError(err.message || 'Sign in failed. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<AuthPageLayout
			headerRight={<AuthHeaderLink to="/signup">Sign up</AuthHeaderLink>}
		>
			<Box sx={{ mb: 3 }}>
				<Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
					{isDemoLogin ? 'Recruiter demo' : 'Welcome back'}
				</Typography>
				<Typography
					variant="body1"
					color="text.secondary"
					sx={{ lineHeight: 1.75 }}
				>
					{isDemoLogin
						? 'Explore the live product with sample Ironpeak Trade Co. data. Soft edits are fine — the org resets weekly.'
						: 'Sign in to manage bids and invoices.'}
				</Typography>
			</Box>

			{wasDeactivated && (
				<Alert severity="info" sx={{ mb: 2 }}>
					This account has been deactivated. Contact
					management@bidzbase.com if you want to come back.
				</Alert>
			)}

			{isDemoLogin && (
				<Alert severity="info" sx={{ mb: 2 }}>
					Use <strong>{PORTFOLIO_DEMO_USERNAME}</strong> /{' '}
					<strong>{PORTFOLIO_DEMO_PASSWORD}</strong>. Email, SMS, and Stripe
					Connect are disabled in this demo.
				</Alert>
			)}

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			<Paper
				elevation={0}
				sx={{
					p: { xs: 2.5, sm: 3 },
					borderRadius: 3,
					border: '1px solid',
					borderColor: 'divider',
				}}
			>
				<Box component="form" onSubmit={handleSubmit}>
					<Stack spacing={2}>
						{isDemoLogin && (
							<Button
								type="button"
								variant="outlined"
								onClick={fillDemoCredentials}
								fullWidth
							>
								Fill demo credentials
							</Button>
						)}

						<TextField
							label="Email"
							name="username"
							type="email"
							value={formData.username}
							onChange={handleChange}
							required
							fullWidth
							autoComplete="username"
							autoFocus={!isDemoLogin}
							placeholder="you@company.com"
						/>

						<Box>
							<TextField
								label="Password"
								name="password"
								type="password"
								value={formData.password}
								onChange={handleChange}
								required
								fullWidth
								autoComplete="current-password"
							/>
							{!isDemoLogin && (
								<Box sx={{ mt: 1, textAlign: 'right' }}>
									<Typography
										component={Link}
										to="/forgot-password"
										variant="body2"
										sx={{
											color: 'primary.main',
											textDecoration: 'none',
											'&:hover': { textDecoration: 'underline' },
										}}
									>
										Forgot password?
									</Typography>
								</Box>
							)}
						</Box>

						<Button
							type="submit"
							variant="contained"
							color="warning"
							size="large"
							disabled={loading}
							fullWidth
							sx={{ py: 1.5 }}
						>
							{loading ? 'Signing in...' : 'Sign in'}
						</Button>
					</Stack>
				</Box>
			</Paper>

			{!isDemoLogin && (
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ textAlign: 'center', mt: 3 }}
				>
					Don&apos;t have an account?{' '}
					<Typography
						component={Link}
						to="/signup"
						variant="body2"
						sx={{
							color: 'primary.main',
							fontWeight: 600,
							textDecoration: 'none',
							'&:hover': { textDecoration: 'underline' },
						}}
					>
						Start 30-day free trial
					</Typography>
				</Typography>
			)}
		</AuthPageLayout>
	);
};

export default Login;
